<script>
import { useVuelidate } from '@vuelidate/core';
import { required, url, minLength } from '@vuelidate/validators';
import wootConstants from 'dashboard/constants/globals';
import { getI18nKey } from 'dashboard/routes/dashboard/settings/helper/settingsHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { EXAMPLE_WEBHOOK_URL } = wootConstants;

// Eventos organizados por categoria
const WEBHOOK_EVENT_CATEGORIES = {
  conversations: {
    labelKey: 'CONVERSATIONS',
    icon: 'chat',
    events: [
      'conversation_created',
      'conversation_status_changed',
      'conversation_updated',
      'conversation_typing_on',
      'conversation_typing_off',
    ],
  },
  messages: {
    labelKey: 'MESSAGES',
    icon: 'message',
    events: ['message_created', 'message_updated'],
  },
  contacts: {
    labelKey: 'CONTACTS',
    icon: 'person',
    events: ['contact_created', 'contact_updated'],
  },
  inbox: {
    labelKey: 'INBOX',
    icon: 'inbox',
    events: ['inbox_created', 'inbox_updated', 'webwidget_triggered'],
  },
  kanban: {
    labelKey: 'KANBAN_FUNNEL',
    icon: 'board',
    events: [
      'funnel_mapping_created',
      'funnel_mapping_updated',
      'funnel_mapping_deleted',
      'funnel_stage_changed',
    ],
  },
  products: {
    labelKey: 'PRODUCTS',
    icon: 'shopping-bag',
    events: [
      'conversation_product_created',
      'conversation_product_updated',
      'conversation_product_deleted',
    ],
  },
  appointments: {
    labelKey: 'APPOINTMENTS',
    icon: 'calendar',
    events: [
      'conversation_appointment_created',
      'conversation_appointment_updated',
      'conversation_appointment_deleted',
    ],
  },
  notes: {
    labelKey: 'NOTES',
    icon: 'note',
    events: [
      'conversation_note_created',
      'conversation_note_updated',
      'conversation_note_deleted',
    ],
  },
};

// Lista plana de todos os eventos para validação
const SUPPORTED_WEBHOOK_EVENTS = Object.values(
  WEBHOOK_EVENT_CATEGORIES
).flatMap(category => category.events);

export default {
  components: {
    NextButton,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      required: true,
    },
  },
  emits: ['submit', 'cancel'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    url: {
      required,
      minLength: minLength(7),
      url,
    },
    subscriptions: {
      required,
    },
  },
  data() {
    return {
      url: this.value.url || '',
      subscriptions: this.value.subscriptions || [],
      eventCategories: WEBHOOK_EVENT_CATEGORIES,
      expandedCategories: {},
    };
  },
  computed: {
    webhookURLInputPlaceholder() {
      return this.$t(
        'INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.PLACEHOLDER',
        {
          webhookExampleURL: EXAMPLE_WEBHOOK_URL,
        }
      );
    },
    selectedCount() {
      return this.subscriptions.length;
    },
    totalEvents() {
      return SUPPORTED_WEBHOOK_EVENTS.length;
    },
  },
  mounted() {
    // Expandir categorias que têm eventos selecionados
    Object.keys(this.eventCategories).forEach(categoryKey => {
      const category = this.eventCategories[categoryKey];
      const hasSelected = category.events.some(event =>
        this.subscriptions.includes(event)
      );
      if (hasSelected) {
        this.expandedCategories[categoryKey] = true;
      }
    });
  },
  methods: {
    onSubmit() {
      this.$emit('submit', {
        url: this.url,
        subscriptions: this.subscriptions,
      });
    },
    getI18nKey,
    toggleCategory(categoryKey) {
      this.expandedCategories[categoryKey] =
        !this.expandedCategories[categoryKey];
    },
    isCategoryExpanded(categoryKey) {
      return this.expandedCategories[categoryKey] || false;
    },
    getCategorySelectedCount(category) {
      return category.events.filter(event => this.subscriptions.includes(event))
        .length;
    },
    selectAllInCategory(category) {
      category.events.forEach(event => {
        if (!this.subscriptions.includes(event)) {
          this.subscriptions.push(event);
        }
      });
    },
    deselectAllInCategory(category) {
      this.subscriptions = this.subscriptions.filter(
        event => !category.events.includes(event)
      );
    },
    isCategoryFullySelected(category) {
      return category.events.every(event => this.subscriptions.includes(event));
    },
    isCategoryPartiallySelected(category) {
      const selectedCount = this.getCategorySelectedCount(category);
      return selectedCount > 0 && selectedCount < category.events.length;
    },
    toggleCategorySelection(category) {
      if (this.isCategoryFullySelected(category)) {
        this.deselectAllInCategory(category);
      } else {
        this.selectAllInCategory(category);
      }
    },
    selectAll() {
      this.subscriptions = [...SUPPORTED_WEBHOOK_EVENTS];
    },
    deselectAll() {
      this.subscriptions = [];
    },
    getCategoryLabel(labelKey) {
      return this.$t(
        `INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.CATEGORIES.${labelKey}`
      );
    },
  },
};
</script>

<template>
  <form class="flex flex-col w-full" @submit.prevent="onSubmit">
    <div class="w-full">
      <label :class="{ error: v$.url.$error }">
        {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.LABEL') }}
        <input
          v-model="url"
          type="text"
          name="url"
          :placeholder="webhookURLInputPlaceholder"
          @input="v$.url.$touch"
        />
        <span v-if="v$.url.$error" class="message">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.END_POINT.ERROR') }}
        </span>
      </label>

      <!-- Header com contagem e ações em massa -->
      <div class="flex items-center justify-between mb-3">
        <label :class="{ error: v$.subscriptions.$error }" class="mb-0">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.LABEL') }}
          <span class="text-xs text-slate-500 dark:text-slate-400 ml-2">
            ({{ selectedCount }}/{{ totalEvents }}
            {{
              $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.SELECTED')
            }})
          </span>
        </label>
        <div class="flex gap-2">
          <button
            type="button"
            class="text-xs text-woot-500 hover:text-woot-600 dark:text-woot-400"
            @click="selectAll"
          >
            {{
              $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.SELECT_ALL')
            }}
          </button>
          <span class="text-slate-300">|</span>
          <button
            type="button"
            class="text-xs text-slate-500 hover:text-slate-600 dark:text-slate-400"
            @click="deselectAll"
          >
            {{
              $t('INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.DESELECT_ALL')
            }}
          </button>
        </div>
      </div>

      <!-- Categorias de eventos -->
      <div
        class="flex flex-col gap-2 mb-4 max-h-[400px] overflow-y-auto pr-1 scrollbar-thin"
      >
        <div
          v-for="(category, categoryKey) in eventCategories"
          :key="categoryKey"
          class="border border-slate-200 dark:border-slate-700 rounded-lg flex-shrink-0"
        >
          <!-- Header da categoria -->
          <div
            class="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors rounded-t-lg"
            :class="{ 'rounded-b-lg': !isCategoryExpanded(categoryKey) }"
            @click="toggleCategory(categoryKey)"
          >
            <div class="flex items-center gap-2">
              <!-- Checkbox da categoria -->
              <input
                type="checkbox"
                :checked="isCategoryFullySelected(category)"
                :indeterminate="isCategoryPartiallySelected(category)"
                class="rounded"
                @click.stop="toggleCategorySelection(category)"
              />
              <span
                class="font-medium text-sm text-slate-700 dark:text-slate-200"
              >
                {{ getCategoryLabel(category.labelKey) }}
              </span>
              <span
                class="text-xs px-1.5 py-0.5 rounded-full"
                :class="
                  getCategorySelectedCount(category) > 0
                    ? 'bg-woot-100 text-woot-600 dark:bg-woot-900 dark:text-woot-300'
                    : 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                "
              >
                {{ getCategorySelectedCount(category) }}/{{
                  category.events.length
                }}
              </span>
            </div>
            <svg
              class="w-4 h-4 text-slate-500 transition-transform duration-200"
              :class="{ 'rotate-180': isCategoryExpanded(categoryKey) }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          <!-- Lista de eventos da categoria -->
          <transition name="expand">
            <div
              v-if="isCategoryExpanded(categoryKey)"
              class="px-3 py-2 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 rounded-b-lg"
            >
              <div
                v-for="event in category.events"
                :key="event"
                class="flex items-center py-1.5"
              >
                <input
                  :id="event"
                  v-model="subscriptions"
                  type="checkbox"
                  :value="event"
                  name="subscriptions"
                  class="mr-2 rounded"
                />
                <label
                  :for="event"
                  class="text-sm text-slate-600 dark:text-slate-300 cursor-pointer flex-1"
                >
                  {{
                    $t(
                      getI18nKey(
                        'INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENTS',
                        event
                      )
                    )
                  }}
                  <span class="text-xs text-slate-400 dark:text-slate-500 ml-1">
                    ({{ event }})
                  </span>
                </label>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
      <NextButton
        faded
        slate
        type="reset"
        :label="$t('INTEGRATION_SETTINGS.WEBHOOK.FORM.CANCEL')"
        @click.prevent="$emit('cancel')"
      />
      <NextButton
        type="submit"
        :disabled="v$.$invalid || isSubmitting"
        :is-loading="isSubmitting"
        :label="submitLabel"
      />
    </div>
  </form>
</template>

<style scoped>
/* Transição suave para expandir/colapsar */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.2s ease-out;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 500px;
}

/* Scrollbar personalizada */
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.5) transparent;
}

.scrollbar-thin::-webkit-scrollbar {
  width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.5);
  border-radius: 3px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background-color: rgba(148, 163, 184, 0.7);
}
</style>
